import React, { useRef, useCallback, useEffect } from 'react';
import { Styles } from './styles';
import { connectInfiniteHits } from 'react-instantsearch-dom';
import { InfiniteHitsProvided } from 'react-instantsearch-core';
import { AlgoliaProduct } from 'models/products';
import { SearchResult } from '../SearchResult/SearchResult';
import { SearchingIndicator } from '../SearchingIndicator/SearchingIndicator';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver';

// When the user has scrolled down such that there are 5 products remaining, we will fetch for more results. The `sentinelIndex` is used to determine how many products are left before we call `refineNext`.
const sentinelIndex = 6;

type HitsConnectedProps = InfiniteHitsProvided<AlgoliaProduct>;

export const HitsConnected: React.FC<HitsConnectedProps> = ({ hits, hasMore, refineNext }) => {
  const sentinelRef = useRef(null);
  const fetchMoreProducts: IntersectionObserverCallback = useCallback(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (hasMore) {
            refineNext();
          }
        }
      });
    },
    [hasMore, refineNext],
  );
  const { updateCallback } = useIntersectionObserver();

  useEffect(() => {
    if (sentinelRef.current) {
      updateCallback(fetchMoreProducts, sentinelRef.current);
    }
  }, [sentinelRef.current, fetchMoreProducts]);

  return (
    <div>
      <Styles.SearchResultsList>
        {hits.map((hit, index) =>
          index === hits.length - sentinelIndex ? (
            <div ref={sentinelRef} key={hit.objectID}>
              <SearchResult hit={hit} />
            </div>
          ) : (
            <SearchResult key={hit.objectID} hit={hit} />
          ),
        )}
      </Styles.SearchResultsList>
      <Styles.LoadMoreArea>
        <SearchingIndicator />
      </Styles.LoadMoreArea>
    </div>
  );
};

export const Hits = connectInfiniteHits(HitsConnected);
